import { useEffect, useRef } from "react";

function getRealtimeUrl(path, token) {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
  const origin = baseUrl.replace(/\/api\/?$/, "");
  if (!origin) return null;

  const wsOrigin = origin.startsWith("https://")
    ? origin.replace("https://", "wss://")
    : origin.replace("http://", "ws://");

  const query = token ? `?token=${encodeURIComponent(token)}` : "";
  return `${wsOrigin}/api/realtime${path}${query}`;
}

export default function useRealtimeChannel({ enabled, path, token, eventType, onMessage }) {
  const callbackRef = useRef(onMessage);

  useEffect(() => {
    callbackRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!enabled || !path) return undefined;

    const url = getRealtimeUrl(path, token);
    if (!url) return undefined;

    let socket = null;
    let pingIntervalId = null;
    let reconnectTimeoutId = null;
    let isDisposed = false;

    const connect = () => {
      if (isDisposed) return;

      socket = new WebSocket(url);

      socket.onopen = () => {
        if (pingIntervalId) window.clearInterval(pingIntervalId);
        pingIntervalId = window.setInterval(() => {
          if (socket?.readyState === WebSocket.OPEN) {
            socket.send("ping");
          }
        }, 25000);
      };

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (!eventType || payload?.type === eventType) {
            callbackRef.current?.(payload);
          }
        } catch {
          // ignore malformed payloads
        }
      };

      socket.onclose = () => {
        if (pingIntervalId) {
          window.clearInterval(pingIntervalId);
          pingIntervalId = null;
        }
        if (!isDisposed) {
          reconnectTimeoutId = window.setTimeout(connect, 5000);
        }
      };
    };

    connect();

    return () => {
      isDisposed = true;
      if (reconnectTimeoutId) window.clearTimeout(reconnectTimeoutId);
      if (pingIntervalId) window.clearInterval(pingIntervalId);
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.close();
      }
    };
  }, [enabled, path, token, eventType]);
}
